import { Link } from "react-router-dom";
import { Ship, Plane, Package, Home as HomeIcon, ShieldCheck, Truck, Search as SearchIcon, Boxes, ArrowRight, MapPin, Clock, Quote } from "lucide-react";
import TrackingBar from "../components/TrackingBar";
import { BRAND } from "../lib/brand";

const services = [
  { icon: Ship, title: "Sea Freight", desc: "FCL & LCL ocean shipping from Canada and Miami to all English Caribbean ports.", img: "https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { icon: Plane, title: "Air Freight", desc: "Express air cargo for time-sensitive shipments.", img: "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { icon: Boxes, title: "Food Barrel Packing", desc: "Professional barrel & box packing, sealing, manifesting.", img: "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { icon: HomeIcon, title: "Door-to-Door", desc: "Pickup from your door, delivery to receiver's door — fully handled." },
  { icon: ShieldCheck, title: "Pre-Clearance", desc: "Customs documentation prepared before arrival." },
  { icon: Truck, title: "Courier Service", desc: "Small parcels & documents to the Caribbean in days, not weeks." },
  { icon: SearchIcon, title: "Sourcing", desc: "Can't find an item? We'll source and ship it for you." },
  { icon: Package, title: "Consolidation", desc: "Multi-shipper LCL consolidation to lower your cost per cubic foot." },
];

const stats = [
  { value: "12+", label: "Caribbean destinations" },
  { value: "3", label: "Origin hubs" },
  { value: "48h", label: "Pickup turnaround" },
  { value: "100%", label: "Cargo handled with care" },
];

const steps = [
  { n: "01", t: "Request Pickup", d: "Submit a pickup request online or call us. We schedule a free pickup." },
  { n: "02", t: "We Pack & Ship", d: "Our team packs your barrels/boxes and books the next sailing or flight." },
  { n: "03", t: "Track Anywhere", d: "Track your shipment in real-time with your unique tracking number." },
  { n: "04", t: "Door Delivery", d: "Pre-cleared cargo delivered straight to your receiver's door." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-[#1E2A78]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1568347877321-f8935c7dc5a3?crop=entropy&cs=srgb&fm=jpg&q=85')` }}
        />
        <div className="absolute inset-0 bg-[#1E2A78]/75" />
        <div className="absolute inset-0 amp-grid-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 w-full">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 text-white">
              <div className="amp-reveal inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-xs font-semibold uppercase tracking-wider mb-6">
                <Ship className="h-3.5 w-3.5" /> Canada & USA → English Caribbean
              </div>
              <h1 className="amp-reveal amp-reveal-delay-1 text-4xl sm:text-5xl lg:text-7xl font-display font-extrabold leading-[1.02] tracking-tight">
                Reliable freight to the <span className="text-[#FFD700]">Caribbean.</span><br />
                Trusted by senders since day one.
              </h1>
              <p className="amp-reveal amp-reveal-delay-2 mt-6 text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed">
                {BRAND.tagline}. Sea, air, courier, barrel packing, pre-clearance and door-to-door — handled end-to-end from Maple Ridge, Mississauga and Miami.
              </p>
              <div className="amp-reveal amp-reveal-delay-3 mt-8 flex flex-wrap gap-3">
                <Link to="/pickup" data-testid="hero-pickup-btn" className="px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2">
                  Request a Pickup <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/services" data-testid="hero-services-btn" className="px-6 py-3 bg-white/10 text-white border border-white/20 font-semibold rounded-sm hover:bg-white/20">
                  Explore Services
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 amp-reveal amp-reveal-delay-4">
              <div className="bg-white rounded-sm shadow-2xl border border-slate-200 p-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#1E2A78] mb-3">Track Your Shipment</div>
                <TrackingBar variant="inline" testId="hero-tracking-bar" />
                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-600">
                  <Clock className="h-3.5 w-3.5 text-[#1E2A78]" />
                  Real-time status updates from origin to receiver's door.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-white border-y border-slate-200 py-6 overflow-hidden">
        <div className="amp-marquee">
          {[...BRAND.destinations, ...BRAND.destinations].map((d, i) => (
            <div key={`${d}-${i}`} className="flex items-center gap-3 text-slate-600 whitespace-nowrap">
              <MapPin className="h-4 w-4 text-[#1E2A78]" />
              <span className="font-display font-semibold uppercase tracking-wider text-sm">{d}</span>
              <span className="text-[#FFD700]">★</span>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#F1F5F9] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {stats.map((s) => (
            <div key={s.label} className="bg-white p-8 sm:p-10">
              <div className="text-4xl sm:text-5xl font-display font-extrabold text-[#1E2A78]">{s.value}</div>
              <div className="mt-2 text-sm text-slate-600 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1E2A78] mb-3">What We Do</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
              End-to-end logistics, <span className="text-[#1E2A78]">handled with care.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              From a single barrel to a full container, Amplartic moves your cargo from North America to the Caribbean with reliable schedules, clear paperwork, and a real human on every call.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {services.map((s, i) => {
              const Icon = s.icon;
              const isWide = i === 0 || i === 3;
              return (
                <div
                  key={s.title}
                  className={`group bg-white p-8 transition-colors hover:bg-[#1E2A78] hover:text-white ${isWide ? "sm:col-span-2 lg:col-span-2" : ""}`}
                  data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {s.img && isWide && (
                    <div className="mb-6 -m-8 mb-6 h-40 overflow-hidden">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className={`h-10 w-10 rounded-sm border border-slate-200 flex items-center justify-center mb-5 group-hover:border-[#FFD700] group-hover:bg-[#FFD700]/20`}>
                    <Icon className="h-5 w-5 text-[#1E2A78] group-hover:text-[#FFD700]" />
                  </div>
                  <h3 className="text-lg font-display font-bold mb-2">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600 group-hover:text-slate-200">{s.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10">
            <Link to="/services" data-testid="home-view-all-services" className="inline-flex items-center gap-2 text-[#1E2A78] font-semibold hover:gap-3 transition-all">
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-24 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700] mb-3">How It Works</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">From your door to theirs in 4 steps.</h2>
            </div>
            <Link to="/pickup" className="px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2 self-start" data-testid="howitworks-cta">
              Start Now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {steps.map((s) => (
              <div key={s.n} className="bg-[#0F172A] p-8">
                <div className="font-mono text-[#FFD700] text-sm mb-3">{s.n}</div>
                <h3 className="text-xl font-display font-bold mb-3">{s.t}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1E2A78] mb-3">Coverage</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
              3 origin hubs.<br />
              <span className="text-[#1E2A78]">Every English Caribbean island.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
              We consolidate from Canada and Miami to ports across the region — with regular sailings and air departures.
            </p>

            <div className="mt-8 space-y-3">
              {BRAND.offices.map((o) => (
                <div key={o.city} className="flex items-center gap-4 p-4 border border-slate-200 rounded-sm hover:border-[#1E2A78] hover:bg-slate-50 transition-colors">
                  <div className="h-10 w-10 bg-[#FFD700] rounded-sm flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[#1E2A78]" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#0F172A]">{o.city}, {o.region}</div>
                    <div className="text-sm text-slate-500">{o.country} • Drop-off & pickup</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#F1F5F9] border border-slate-200 rounded-sm p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1E2A78] mb-4">We Ship To</div>
            <div className="grid grid-cols-2 gap-3">
              {BRAND.destinations.map((d) => (
                <div key={d} className="flex items-center gap-2 text-sm text-slate-700">
                  <div className="h-1.5 w-1.5 rounded-full bg-[#FFD700]" />
                  {d}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 sm:py-24 bg-[#1E2A78] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="h-12 w-12 text-[#FFD700] mx-auto mb-6" />
          <p className="text-2xl sm:text-3xl font-display font-medium leading-snug">
            "Amplartic packed our barrels, pre-cleared customs and delivered to my mother's door in Kingston. Smoothest shipment we've done in years."
          </p>
          <div className="mt-8 text-sm uppercase tracking-wider text-[#FFD700]">— Repeat customer, Mississauga → Kingston</div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FFD700] rounded-sm p-8 sm:p-12 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-display font-extrabold text-[#1E2A78]">Ready to ship?</h3>
              <p className="mt-2 text-[#1E2A78]/80">Get a free pickup quote today. No hidden fees.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/pickup" data-testid="cta-pickup" className="px-6 py-3 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E]">
                Request Pickup
              </Link>
              <a href={BRAND.phoneHref} data-testid="cta-phone" className="px-6 py-3 bg-white text-[#1E2A78] font-semibold rounded-sm hover:bg-slate-100 inline-flex items-center gap-2">
                Call {BRAND.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
